import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, MessageSquareText, Download, Crown, Sparkles, History } from "lucide-react";

export function InterviewPrepDialog({ app, isPro }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [preps, setPreps] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const canPrep = !!(app?.tailored_cv_id && app?.job_description);
  const doneList = preps.filter((p) => p.status === "done");
  const viewed = doneList.find((p) => p.id === selectedId) || doneList[0] || null;
  const ready = !!viewed;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/interview/by-application/${app.id}`);
      setPreps(data);
      const firstDone = data.find((p) => p.status === "done");
      if (firstDone) setSelectedId((cur) => cur || firstDone.id);
    } catch (e) { /* pro gate handled in UI */ }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open && isPro && canPrep) load(); /* eslint-disable-next-line */ }, [open]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/interview/generate", { application_id: app.id });
      const id = data.id;
      let finished = false;
      for (let i = 0; i < 80; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: p } = await api.get(`/interview/${id}`);
        if (p.status === "done") { toast.success("Interview prep ready"); setSelectedId(id); finished = true; break; }
        if (p.status === "error") { toast.error("Generation failed. Please try again."); finished = true; break; }
      }
      if (!finished) toast.message("Still working — check back shortly.");
      await load();
      setSelectedId((cur) => id || cur);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Generation failed");
    } finally { setGenerating(false); }
  };

  const role = (viewed?.questions || []).filter((q) => q.category === "role");
  const generic = (viewed?.questions || []).filter((q) => q.category === "generic");

  const fmt = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <>
      <Button
        variant="ghost" size="icon"
        className="h-8 w-8 text-[#166534] disabled:opacity-40"
        onClick={() => setOpen(true)}
        disabled={!canPrep}
        title={canPrep ? "Interview Prep" : "Tailor a CV for this application first."}
        data-testid={`interview-prep-${app.id}`}
      >
        <MessageSquareText className="h-4 w-4" strokeWidth={1.5} />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="interview-prep-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><MessageSquareText className="h-5 w-5 text-[#166534]" /> Interview Prep — {app?.job_title} · {app?.company}</DialogTitle></DialogHeader>

          {!isPro ? (
            <div className="text-center py-6" data-testid="interview-pro-gate">
              <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-3"><Crown className="h-6 w-6 text-accent" /></div>
              <h3 className="font-heading text-xl font-bold">Interview Prep is a Pro feature</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">Generate 17 tailored questions with model answers and quick talking points, grounded in your tailored CV.</p>
              <Button className="mt-4" onClick={() => navigate("/billing")} data-testid="interview-upgrade-btn"><Crown className="h-4 w-4 mr-1.5" /> Upgrade to Pro</Button>
            </div>
          ) : loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : !ready ? (
            <div className="text-center py-6">
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">We'll generate 12 role-specific + 5 general interview questions with full answers and talking points, grounded entirely in your tailored CV for this role.</p>
              <Button onClick={generate} disabled={generating} data-testid="interview-generate-btn" className="active:scale-[0.98] transition-transform">
                {generating ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Generating…</> : <><Sparkles className="h-4 w-4 mr-1.5" /> Generate interview prep</>}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">2 generations/day · 1 per job/day</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 justify-between">
                {doneList.length > 1 ? (
                  <div className="flex items-center gap-2" data-testid="interview-history">
                    <History className="h-4 w-4 text-muted-foreground" />
                    <Select value={viewed.id} onValueChange={setSelectedId}>
                      <SelectTrigger className="h-9 w-56 text-xs" data-testid="interview-history-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {doneList.map((p, i) => <SelectItem key={p.id} value={p.id}>{i === 0 ? "Latest · " : ""}{fmt(p.created_at)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ) : <span className="text-xs text-muted-foreground">Prepared {fmt(viewed.created_at)}</span>}
                <Button variant="outline" size="sm" onClick={generate} disabled={generating} data-testid="interview-regenerate-btn">
                  {generating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} New prep
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                <a href={`${API}/interview/${viewed.id}/download?doc=full`} target="_blank" rel="noreferrer" data-testid="interview-dl-full"><Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1.5" /> Full answers PDF</Button></a>
                <a href={`${API}/interview/${viewed.id}/download?doc=points`} target="_blank" rel="noreferrer" data-testid="interview-dl-points"><Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1.5" /> Talking points PDF</Button></a>
              </div>
              <Tabs defaultValue="full">
                <TabsList className="w-full"><TabsTrigger value="full" className="flex-1" data-testid="tab-full">Full answers</TabsTrigger><TabsTrigger value="points" className="flex-1" data-testid="tab-points">Talking points</TabsTrigger></TabsList>
                <TabsContent value="full"><QList items={[...role, ...generic]} mode="full" /></TabsContent>
                <TabsContent value="points"><QList items={[...role, ...generic]} mode="points" /></TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function QList({ items, mode }) {
  return (
    <div className="space-y-4 mt-3">
      {items.map((q, i) => (
        <div key={i} className="border-b border-zinc-100 pb-3" data-testid={`interview-q-${i}`}>
          <p className="font-semibold text-sm">{i + 1}. {q.question} {q.behavioral && <span className="text-[10px] uppercase font-bold text-accent bg-accent/10 rounded-full px-2 py-0.5 ml-1">STAR</span>}</p>
          {mode === "full" ? (
            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{q.answer}</p>
          ) : (
            <ul className="text-sm text-muted-foreground mt-1 space-y-0.5">{(q.points || []).map((p, j) => <li key={j}>• {p}</li>)}</ul>
          )}
        </div>
      ))}
    </div>
  );
}
